import { useAuth } from "@/lib/AuthContext";
import { getTrialState, getTrialCopy } from "@/lib/trialUtils";
import { Sparkles, AlertTriangle, Clock, ChevronRight } from "lucide-react";

/**
 * Sticky trial countdown banner. Mounts at the top of every authenticated
 * page (rendered inside AppLayout's <main>, above the <Outlet>).
 *
 * Visual hierarchy follows the severity returned by `getTrialState`:
 *   info (4-7d)   → calm sky/amber gradient with a Sparkles icon
 *   warning (2-3d)→ orange gradient with a Clock icon
 *   urgent (<1d)  → red gradient with an AlertTriangle, pulses subtly
 *   expired       → solid red, no CTA escape
 *
 * Returns null for any user not on a trial (free, paid, family seat,
 * admin, trainer) so the banner doesn't take up vertical real estate
 * for the 95% of users it doesn't apply to.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const state = getTrialState(user);
  if (!state) return null;
  const copy = getTrialCopy(state);

  // Tier-based palette. Pure CSS — no JS animations except a subtle
  // pulse on the urgent variant.
  const palette = {
    info: {
      wrap:
        "bg-gradient-to-r from-amber-500/15 via-sky-500/15 to-amber-500/15 border-amber-400/30",
      icon: Sparkles,
      iconCls: "text-amber-300",
      headline: "text-amber-100",
    },
    warning: {
      wrap:
        "bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-orange-500/20 border-orange-400/40",
      icon: Clock,
      iconCls: "text-orange-300",
      headline: "text-orange-50",
    },
    urgent: {
      wrap:
        "bg-gradient-to-r from-rose-500/25 via-red-500/25 to-rose-500/25 border-rose-400/50 animate-pulse",
      icon: AlertTriangle,
      iconCls: "text-rose-200",
      headline: "text-rose-50",
    },
    expired: {
      wrap: "bg-rose-600/25 border-rose-500/60",
      icon: AlertTriangle,
      iconCls: "text-rose-100",
      headline: "text-white",
    },
  }[state.severity];

  const Icon = palette.icon;
  const handleClick = () => {
    // Sibling owns the pricing / billing surface. Open in a new tab so
    // the user keeps their in-app context.
    const url = "https://gotafitness.com/pricing";
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="trial-banner"
      data-severity={state.severity}
      className={`group w-full rounded-2xl border ${palette.wrap} px-4 py-3 mb-4 flex items-center gap-3 text-left transition active:scale-[0.99]`}
    >
      <div
        className={`shrink-0 w-9 h-9 rounded-full bg-black/30 border border-white/10 flex items-center justify-center ${palette.iconCls}`}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium leading-snug ${palette.headline}`}
          data-testid="trial-banner-headline"
        >
          {copy.headline}
        </p>
        {!state.expired ? (
          <p className="text-[11px] text-white/55 mt-0.5">
            Trial ends {state.endsAt.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })}
          </p>
        ) : null}
      </div>
      <span
        data-testid="trial-banner-cta"
        className="shrink-0 text-xs font-semibold text-white/90 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition"
      >
        {copy.cta}
        <ChevronRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}
